<template>
  <header class="blog-header">
    <div class="container">
      <div class="header-content">
        <div class="logo">
          <a href="/" class="logo-link">
            <span class="logo-icon">陌</span>
            <span class="logo-text">Blog</span>
          </a>
        </div>
        <nav class="main-nav">
          <ul>
            <li><router-link to="/" class="nav-link">首页</router-link></li>
            <li><router-link to="/tool" class="nav-link">工具</router-link></li>
            <li><router-link to="/messages" class="nav-link">留言板</router-link></li>
            <li><router-link to="/about" class="nav-link">网站</router-link></li>
          </ul>
        </nav>
        <div class="search-icon">
          <button class="search-btn" @click="toggleSearch">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="2"/>
              <path d="M21 21l-4.35-4.35" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        <!-- 登录入口 -->
        <div class="user-auth">
          <!-- 未登录状态 -->
          <button v-if="!isLoggedIn" class="login-btn" @click="handleOpenLoginDialog">
            登录
          </button>
          
          <!-- 已登录状态 -->
          <div v-else class="user-profile">
            <div class="avatar-container" @click="toggleUserMenu">
              <!-- 图片头像 -->
              <img 
                v-if="!avatarError" 
                :src="userInfo.avatar" 
                :alt="userInfo.username" 
                class="user-avatar"
                @error="handleAvatarError"
              >
              <!-- 首字母头像（图片加载失败时显示） -->
              <div v-else class="avatar-fallback" :style="avatarFallbackStyle">
                {{ avatarInitial }}
              </div>
              <span class="avatar-arrow" :class="{ 'rotated': showUserMenu }"></span>
            </div>
            
            <!-- 用户菜单 -->
            <div v-if="showUserMenu" class="user-menu">
              <ul>
                <li><a href="#" class="menu-item">个人中心</a></li>
                <li><a href="#" class="menu-item">设置</a></li>
                <li><a href="#" class="menu-item" @click.prevent="logout">退出登录</a></li>
              </ul>
            </div>
          </div>
        </div>
        <div class="mobile-menu-btn">
          <button @click="toggleMobileMenu">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 12h18M3 6h18M3 18h18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        
      </div>
    </div>
    <!-- 搜索框组件 -->
    <div class="search-panel" v-show="showSearch">
      <div class="container">
        <div class="search-content">
          <div class="search-input-wrapper">
            <input 
              type="text" 
              placeholder="搜索文章..." 
              class="search-input" 
              v-model="searchText"
              @keyup.enter="handleSearch"
              ref="searchInput"
            >
            <button class="search-submit-btn" @click="handleSearch">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="2"/>
                <path d="M21 21l-4.35-4.35" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
          <button class="search-close-btn" @click="toggleSearch">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <path d="M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
    <!-- 移动端菜单 -->
    <div class="mobile-menu" v-show="showMobileMenu">
      <nav>
        <ul>
          <li><router-link to="/" class="mobile-nav-link">首页</router-link></li>
          <li><router-link to="/tool" class="mobile-nav-link">工具</router-link></li>
          <li><router-link to="/messages" class="mobile-nav-link">留言板</router-link></li>
          <li><router-link to="/about" class="mobile-nav-link">网站</router-link></li>
        </ul>
      </nav>
    </div>
  </header>
  
  <!-- 登录对话框 -->
  <div v-if="showLoginDialog" class="login-dialog-overlay" @click="closeLoginDialog">
    <div class="login-dialog" @click.stop>
      <!-- 品牌头部 -->
      <div class="login-brand">
        <div class="brand-logo">陌</div>
        <h2 class="brand-title">欢迎回来</h2>
        <p class="brand-subtitle">登录陌攻博客，开启你的创作之旅</p>
      </div>

      <!-- 关闭按钮 -->
      <button class="close-btn" @click="closeLoginDialog" aria-label="关闭">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>

      <!-- 表单区域 -->
      <div class="dialog-content">
        <form @submit.prevent="handleLogin">
          <!-- 用户名 -->
          <div class="form-group">
            <label for="username" class="form-label">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              <span>用户名 / 邮箱</span>
            </label>
            <input 
              type="text" 
              id="username" 
              v-model="loginForm.username"
              class="form-input"
              :class="{ 'has-error': loginErrors.username }"
              placeholder="请输入用户名或邮箱"
              autocomplete="username"
            >
            <span v-if="loginErrors.username" class="error-tip">{{ loginErrors.username }}</span>
          </div>

          <!-- 密码 -->
          <div class="form-group">
            <label for="password" class="form-label">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
              <span>密码</span>
            </label>
            <input 
              type="password" 
              id="password" 
              v-model="loginForm.password"
              class="form-input"
              :class="{ 'has-error': loginErrors.password }"
              placeholder="请输入密码"
              autocomplete="current-password"
            >
            <span v-if="loginErrors.password" class="error-tip">{{ loginErrors.password }}</span>
          </div>

          <!-- 验证码 -->
          <div class="form-group">
            <label for="captchaCode" class="form-label">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M9 12l2 2 4-4"></path>
                <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c2.39 0 4.68.94 6.36 2.64"></path>
              </svg>
              <span>验证码</span>
            </label>
            <div class="captcha-row">
              <input 
                type="text" 
                id="captchaCode" 
                v-model="loginForm.captchaCode"
                class="form-input captcha-input"
                :class="{ 'has-error': loginErrors.captchaCode }"
                placeholder="请输入验证码"
                maxlength="4"
                autocomplete="off"
              >
              <div class="captcha-image-wrap" @click="refreshCaptcha" title="点击刷新">
                <img 
                  :src="captchaImageUrl" 
                  class="captcha-image"
                  alt="验证码"
                >
                <div class="captcha-refresh-mask">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="23 4 23 10 17 10"></polyline>
                    <polyline points="1 20 1 14 7 14"></polyline>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                  </svg>
                  <span>换一张</span>
                </div>
              </div>
            </div>
            <span v-if="loginErrors.captchaCode" class="error-tip">{{ loginErrors.captchaCode }}</span>
          </div>

          <!-- 选项行 -->
          <div class="form-actions">
            <label class="remember-checkbox">
              <input type="checkbox" v-model="loginForm.remember">
              <span class="checkbox-custom"></span>
              <span class="remember-text">记住我</span>
            </label>
            <a href="#" class="forgot-link" @click.prevent>忘记密码？</a>
          </div>

          <!-- 提交按钮 -->
          <button type="submit" class="login-submit-btn" :disabled="isSubmitting">
            <span v-if="!isSubmitting">登 录</span>
            <span v-else class="loading-text">
              <span class="spinner"></span>
              登录中...
            </span>
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { authApi } from '@/api/authApi'
import { getUserInfo, setUserInfo, clearUserInfo } from '@/utils/request'
import request from '@/utils/request'

export default {
  name: 'BlogHeader',
  data() {
    return {
      showMobileMenu: false,
      showSearch: false,
      searchText: '', // 搜索文本
      // 登录相关状态
      isLoggedIn: false,
      userInfo: {
        username: '',
        avatar: ''
      },
      showLoginDialog: false,
      showUserMenu: false,
      loginForm: {
        username: '',
        password: '',
        remember: false,
        captchaKey: '',  // 验证码key
        captchaCode: ''  // 用户输入的验证码
      },
      loginErrors: {
        username: '',
        password: '',
        captchaCode: ''
      },
      isSubmitting: false,
      avatarError: false, // 头像图片加载失败标记
      captchaKey: ''  // 当前验证码key
    }
  },
  computed: {
    // 用户名首字母（中文取第一个字，英文取首字母大写）
    avatarInitial() {
      const username = this.userInfo?.username || this.userInfo?.nickname || 'U'
      if (!username) return 'U'
      // 取第一个字符
      return username.charAt(0).toUpperCase()
    },
    // 首字母头像背景色（根据用户名生成稳定的颜色）
    avatarFallbackStyle() {
      const username = this.userInfo?.username || 'default'
      // 根据用户名生成一个稳定的颜色索引
      const colors = [
        '#1abc9c', '#2ecc71', '#3498db', '#9b59b6', '#34495e',
        '#16a085', '#27ae60', '#2980b9', '#8e44ad', '#2c3e50',
        '#f39c12', '#e74c3c', '#e67e22', '#d35400', '#c0392b'
      ]
      let hash = 0
      for (let i = 0; i < username.length; i++) {
        hash = username.charCodeAt(i) + ((hash << 5) - hash)
      }
      const colorIndex = Math.abs(hash) % colors.length
      return {
        backgroundColor: colors[colorIndex]
      }
    },
    // 验证码图片URL
    captchaImageUrl() {
      if (!this.captchaKey) {
        return ''
      }
      return `/api/captcha/image/${this.captchaKey}`
    }
  },
  mounted() {
    // 确保在滚动时也保持半透明状态
    window.addEventListener('scroll', this.handleScroll);
    
    // 页面加载时恢复滚动位置
    this.restoreScrollPosition();
    
    // 初始化登录状态
    this.initLoginState();

    // 监听打开登录对话框的事件
    window.addEventListener('open-login-dialog', this.handleOpenLoginDialog);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('open-login-dialog', this.handleOpenLoginDialog);
  },
  methods: {
    toggleMobileMenu() {
      this.showMobileMenu = !this.showMobileMenu
    },
    // 处理打开登录对话框事件
    handleOpenLoginDialog() {
      this.showLoginDialog = true
      // 打开对话框时获取验证码
      this.refreshCaptcha()
    },
    toggleSearch() {
      this.showSearch = !this.showSearch
      // 打开搜索框时自动聚焦
      if (this.showSearch) {
        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus()
          }
        })
      } else {
        // 关闭时清空搜索文本
        this.searchText = ''
      }
    },
    // 处理搜索
    handleSearch() {
      if (!this.searchText.trim()) {
        return
      }
      // 关闭搜索框
      this.showSearch = false
      // 跳转到搜索页面并传递搜索参数
      this.$router.push({
        path: '/search',
        query: { q: this.searchText.trim() }
      })
    },
    handleScroll() {
      // 确保导航栏始终保持半透明，防止滚动时被覆盖或样式改变
      const header = document.querySelector('.blog-header');
      if (header) {
        header.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
      }
      
      // 保存滚动位置到localStorage
      localStorage.setItem('scrollPosition', window.scrollY.toString());
    },
    restoreScrollPosition() {
      // 从localStorage获取并恢复滚动位置
      const savedPosition = localStorage.getItem('scrollPosition');
      // 只在保存的位置大于0时才恢复，避免页面总是滚动到特定区域
      if (savedPosition && parseInt(savedPosition) > 50) {
        // 使用setTimeout确保页面内容已加载
        setTimeout(() => {
          window.scrollTo({ top: parseInt(savedPosition), behavior: 'smooth' });
        }, 100);
      }
    },
    // 初始化登录状态（从 Cookie / localStorage / sessionStorage 恢复）
    initLoginState() {
      const savedUser = getUserInfo();
      if (savedUser) {
        this.isLoggedIn = true;
        this.userInfo = savedUser;
        this.avatarError = false; // 重置头像错误状态，让图片尝试加载
      }
    },
    // 切换用户菜单
    toggleUserMenu() {
      this.showUserMenu = !this.showUserMenu
    },
    // 头像图片加载失败处理 - 显示首字母fallback头像
    handleAvatarError() {
      console.log('Avatar image failed to load, showing fallback')
      this.avatarError = true
    },
    // 关闭登录对话框
    closeLoginDialog() {
      this.showLoginDialog = false;
      // 重置表单
      this.loginForm = {
        username: '',
        password: '',
        remember: false,
        captchaKey: '',
        captchaCode: ''
      };
      this.loginErrors = {
        username: '',
        password: '',
        captchaCode: ''
      };
      // 清空验证码key
      this.captchaKey = '';
    },
    // 获取新验证码
    async refreshCaptcha() {
      try {
        const response = await request({
          url: '/api/captcha/key',
          method: 'get'
        })
        if (response) {
          this.captchaKey = response
          this.loginForm.captchaKey = response
          this.loginForm.captchaCode = '' // 清空验证码输入
        }
      } catch (error) {
        console.error('获取验证码key失败:', error)
      }
    },
    // 验证登录表单
    validateLoginForm() {
      let isValid = true;
      
      // 重置错误信息
      this.loginErrors = {
        username: '',
        password: '',
        captchaCode: ''
      };
      
      // 验证用户名/邮箱
      if (!this.loginForm.username.trim()) {
        this.loginErrors.username = '请输入用户名或邮箱';
        isValid = false;
      }
      
      // 验证密码
      if (!this.loginForm.password) {
        this.loginErrors.password = '请输入密码';
        isValid = false;
      } else if (this.loginForm.password.length < 6) {
        this.loginErrors.password = '密码长度不能少于6位';
        isValid = false;
      }
      
      // 验证验证码
      if (!this.loginForm.captchaCode.trim()) {
        this.loginErrors.captchaCode = '请输入验证码';
        isValid = false;
      }
      
      return isValid;
    },
    // 处理登录
    async handleLogin() {
      // 验证表单
      if (!this.validateLoginForm()) {
        return;
      }
      
      this.isSubmitting = true;
      
      try {
        // 调用登录API（携带验证码参数）
        console.log('开始登录请求:', this.loginForm.username)
        const response = await authApi.login({
          username: this.loginForm.username,
          password: this.loginForm.password,
          remember: this.loginForm.remember,
          captchaKey: this.loginForm.captchaKey,
          captchaCode: this.loginForm.captchaCode
        })
        
        console.log('登录响应:', response)
        
        if (response.success && response.user) {
          // 登录成功
          this.isLoggedIn = true;
          this.userInfo = {
            username: response.user.username,
            avatar: response.user.avatar
          };
          this.showLoginDialog = false;
          this.avatarError = false; // 重置头像错误状态
          
          // 保存登录状态到 Cookie（30天有效，关闭浏览器不丢失）
          setUserInfo(this.userInfo)
          
          // 重置表单
          this.loginForm = {
            username: '',
            password: '',
            remember: false,
            captchaKey: '',
            captchaCode: ''
          };
          this.loginErrors = {
            username: '',
            password: '',
            captchaCode: ''
          };
          
          // 通知其他组件登录成功
          window.dispatchEvent(new CustomEvent('user-login-success', {
            detail: { user: this.userInfo }
          }))
        }
      } catch (error) {
        console.error('登录失败:', error);
        // 判断是否是验证码错误
        if (error.message && (error.message.includes('验证码') || error.code === 400)) {
          this.loginErrors.captchaCode = error.message;
          // 刷新验证码
          this.refreshCaptcha();
        } else {
          this.loginErrors.password = error.message || '用户名或密码错误';
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    // 退出登录
    async logout() {
      try {
        // 调用退出登录API
        await authApi.logout()
      } catch (error) {
        console.error('退出登录失败:', error);
      } finally {
        // 无论API是否成功，都清除本地状态
        this.isLoggedIn = false;
        this.userInfo = {
          username: '',
          avatar: ''
        };
        this.showUserMenu = false;
        
        // 清除登录状态（Cookie + localStorage + sessionStorage）
        clearUserInfo()

        // 通知其他组件退出登录成功
        window.dispatchEvent(new CustomEvent('user-logout-success'))
      }
    }
  }
}
</script>

<style scoped>
.blog-header {
  background-color: rgba(255, 255, 255, 0.9);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 0;
  z-index: 1000;
  transition: background-color 0.3s ease;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
}

/* 导航链接居中 */
.main-nav {
  flex: 1;
  display: flex;
  justify-content: center;
}

.logo-link {
  display: flex;
  align-items: center;
  text-decoration: none;
  color: #333;
}

.logo-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background-color: #000;
  color: #fff;
  font-weight: bold;
  font-size: 16px;
  border-radius: 4px;
  margin-right: 8px;
}

.logo-text {
  font-size: 18px;
  font-weight: 600;
}

.main-nav ul {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
}

.main-nav li {
  margin-left: 32px;
}

.nav-link {
  color: #8b8a8a;
  text-decoration: none;
  font-size: 16px;
  font-weight: 400;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  transition: color 0.3s ease;
}

.nav-link:hover {
  color: #000;
}

.nav-link.active {
  color: #000;
  font-weight: 500;
}

.search-icon {
  display: flex;
  align-items: center;
}

.search-btn {
  background: none;
  border: none;
  color: #333;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
}

.search-btn:hover {
  color: #000;
}

/* 登录相关样式 */
.user-auth {
  display: flex;
  align-items: center;
  margin-left: 20px;
}

.login-btn {
  padding: 8px 16px;
  border: 1px solid #d0d0d0;
  background-color: white;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #333;
}

.login-btn:hover {
  border-color: #000;
  color: #000;
}

/* 用户资料 */
.user-profile {
  position: relative;
}

/* 头像容器 - 包含头像和下拉箭头 */
.avatar-container {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 4px 8px; /* 上下4px，左右8px，使容器内内容左右对称 */
  border-radius: 20px; /* 圆角矩形（药丸形状），hover时背景阴影对称 */
  transition: background-color 0.3s ease;
  gap: 4px; /* 头像与箭头之间的间距，替代margin实现统一控制 */
}

.avatar-container:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

/* 用户头像图片 */
.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}

/* 首字母头像样式 - 图片加载失败时的fallback */
.avatar-fallback {
  width: 32px;
  height: 32px;
  border-radius: 50%; /* 圆形背景 */
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  text-transform: uppercase;
  line-height: 32px; /* 与容器高度一致，确保字母垂直居中 */
  flex-shrink: 0; /* 防止flex布局时被压缩 */
  text-align: center; /* 水平居中 */
}

/* 下拉箭头 */
.avatar-arrow {
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 4px solid #666;
  transition: transform 0.3s ease;
  flex-shrink: 0; /* 防止flex布局时被压缩 */
}

.avatar-arrow.rotated {
  transform: rotate(180deg);
}

/* 用户菜单 */
.user-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  min-width: 160px;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

.user-menu ul {
  list-style: none;
  margin: 0;
  padding: 8px 0;
}

.user-menu li {
  margin: 0;
}

.menu-item {
  display: block;
  padding: 10px 16px;
  color: #333;
  text-decoration: none;
  font-size: 14px;
  transition: background-color 0.2s ease;
}

.menu-item:hover {
  background-color: #f5f5f5;
}

/* 登录对话框 —— 精致文艺风 */
.login-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(20, 20, 30, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: dialogFadeIn 0.25s ease-out;
}

.login-dialog {
  position: relative;
  width: 90%;
  max-width: 420px;
  background: #fffdf8;
  border-radius: 20px;
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.18),
    0 2px 8px rgba(0, 0, 0, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  overflow: hidden;
  animation: dialogSlideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}

/* 品牌头部 */
.login-brand {
  padding: 36px 32px 24px;
  text-align: center;
  background: linear-gradient(180deg, #faf7ef 0%, #fffdf8 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
}

.brand-logo {
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  background: linear-gradient(135deg, #2d5016 0%, #4a7c2a 100%);
  color: #fff;
  font-size: 26px;
  font-weight: 700;
  font-family: 'STSong', 'SimSun', serif;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px rgba(45, 80, 22, 0.25);
  letter-spacing: 1px;
}

.brand-title {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 600;
  color: #2c3e2d;
  letter-spacing: 0.5px;
}

.brand-subtitle {
  margin: 0;
  font-size: 13px;
  color: #8a9a8b;
  font-weight: 400;
}

/* 关闭按钮 */
.close-btn {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 32px;
  height: 32px;
  background: rgba(0, 0, 0, 0.04);
  border: none;
  border-radius: 50%;
  color: #999;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 2;
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.08);
  color: #333;
  transform: rotate(90deg);
}

/* 表单内容 */
.dialog-content {
  padding: 24px 32px 32px;
}

.form-group {
  margin-bottom: 18px;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #4a5a4b;
}

.form-label svg {
  color: #6b8e5a;
}

.form-input {
  width: 100%;
  padding: 11px 14px;
  border: 1px solid #e5e2d8;
  border-radius: 10px;
  font-size: 14px;
  color: #2c3e2d;
  background: #fdfcf7;
  transition: all 0.25s ease;
  box-sizing: border-box;
  font-family: inherit;
}

.form-input::placeholder {
  color: #b5b0a0;
}

.form-input:focus {
  outline: none;
  border-color: #6b8e5a;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(107, 142, 90, 0.1);
}

.form-input.has-error {
  border-color: #d9534f;
  background: #fff5f5;
}

.form-input.has-error:focus {
  box-shadow: 0 0 0 3px rgba(217, 83, 79, 0.1);
}

.error-tip {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: #d9534f;
  padding-left: 2px;
  animation: shake 0.4s ease-in-out;
}

/* 验证码区域 */
.captcha-row {
  display: flex;
  align-items: stretch;
  gap: 12px;
}

.captcha-input {
  flex: 1;
  min-width: 0;
}

.captcha-image-wrap {
  position: relative;
  width: 110px;
  height: 42px;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid #e5e2d8;
  background: #f5f3eb;
  flex-shrink: 0;
  transition: all 0.25s ease;
}

.captcha-image-wrap:hover {
  border-color: #6b8e5a;
  box-shadow: 0 0 0 3px rgba(107, 142, 90, 0.1);
}

.captcha-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.captcha-refresh-mask {
  position: absolute;
  inset: 0;
  background: rgba(45, 80, 22, 0.75);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 11px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.captcha-image-wrap:hover .captcha-refresh-mask {
  opacity: 1;
}

/* 选项行 */
.form-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 22px;
  margin-top: -2px;
}

.remember-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
  font-size: 13px;
  color: #6b7a6c;
}

.remember-checkbox input[type="checkbox"] {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.checkbox-custom {
  width: 16px;
  height: 16px;
  border: 1.5px solid #c8c4b8;
  border-radius: 4px;
  background: #fdfcf7;
  transition: all 0.2s ease;
  position: relative;
  flex-shrink: 0;
}

.remember-checkbox input[type="checkbox"]:checked + .checkbox-custom {
  background: #6b8e5a;
  border-color: #6b8e5a;
}

.remember-checkbox input[type="checkbox"]:checked + .checkbox-custom::after {
  content: '';
  position: absolute;
  left: 4px;
  top: 1px;
  width: 5px;
  height: 9px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.remember-text {
  font-size: 13px;
}

.forgot-link {
  font-size: 13px;
  color: #6b8e5a;
  text-decoration: none;
  transition: color 0.2s ease;
}

.forgot-link:hover {
  color: #4a7c2a;
  text-decoration: underline;
}

/* 登录按钮 */
.login-submit-btn {
  width: 100%;
  padding: 13px;
  background: linear-gradient(135deg, #2d5016 0%, #4a7c2a 100%);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 4px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(45, 80, 22, 0.25);
  font-family: inherit;
}

.login-submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(45, 80, 22, 0.32);
  background: linear-gradient(135deg, #234011 0%, #3d6820 100%);
}

.login-submit-btn:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(45, 80, 22, 0.25);
}

.login-submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.loading-text {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  letter-spacing: 2px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

/* 动画 */
@keyframes dialogFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes dialogSlideUp {
  from {
    opacity: 0;
    transform: translateY(24px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-4px); }
  40% { transform: translateX(4px); }
  60% { transform: translateX(-3px); }
  80% { transform: translateX(3px); }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 搜索框样式 */
.search-panel {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background-color: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  z-index: 1001;
  animation: slideInLeft 0.3s ease-out;
}

.search-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  flex: 1;
  max-width: 600px;
  position: relative;
}

.search-input {
  width: 100%;
  padding: 12px 48px 12px 20px;
  border: 1px solid #e0e0e0;
  border-radius: 24px;
  font-size: 16px;
  outline: none;
  transition: border-color 0.3s ease;
}

.search-input:focus {
  border-color: #000;
}

.search-submit-btn {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
}

.search-submit-btn:hover {
  color: #000;
}

.search-close-btn {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
}

.search-close-btn:hover {
  color: #000;
}

/* 从左侧滑入动画 */
@keyframes slideInLeft {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
}

.mobile-menu-btn {
  display: none;
}

.mobile-menu-btn button {
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-menu {
  position: fixed;
  top: 64px;
  left: 0;
  right: 0;
  background-color: #fff;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  padding: 20px;
  z-index: 999;
  animation: slideDown 0.3s ease-out;
}

.mobile-menu ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.mobile-menu li {
  margin-bottom: 15px;
}

.mobile-nav-link {
  color: #333;
  text-decoration: none;
  font-size: 16px;
  display: block;
  padding: 8px 0;
  transition: color 0.3s ease;
}

.mobile-nav-link:hover {
  color: #000;
  font-weight: 600;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 992px) {
  .main-nav li {
    margin-left: 16px;
  }
  
  .nav-link {
    font-size: 13px;
  }
  
  .user-auth {
    margin-left: 16px;
  }
  
  .login-btn {
    padding: 6px 12px;
    font-size: 13px;
  }
  
  .user-avatar {
    width: 28px;
    height: 28px;
  }
  
  /* 首字母头像 - 992px断点响应式样式 */
  .avatar-fallback {
    width: 28px;
    height: 28px;
    font-size: 12px;
    line-height: 28px; /* 与容器高度一致，确保字母垂直居中 */
    flex-shrink: 0; /* 防止flex布局时被压缩 */
    text-align: center; /* 水平居中 */
    padding-bottom: 1px; /* 微调视觉基线 */
  }
}

@media (max-width: 768px) {
  .main-nav {
    display: none;
  }
  
  .mobile-menu-btn {
    display: block;
  }
  
  .header-content {
    height: 56px;
  }
  
  .logo-icon {
    width: 24px;
    height: 24px;
    font-size: 14px;
  }
  
  .logo-text {
    font-size: 16px;
  }
  
  .mobile-menu {
    top: 56px;
  }
  
  .user-auth {
    margin-left: 12px;
  }
  
  .login-btn {
    padding: 6px 12px;
    font-size: 12px;
  }
  
  .user-avatar {
    width: 28px;
    height: 28px;
  }
  
  /* 首字母头像 - 768px断点响应式样式 */
  .avatar-fallback {
    width: 28px;
    height: 28px;
    font-size: 12px;
    line-height: 28px; /* 与容器高度一致，确保字母垂直居中 */
    flex-shrink: 0; /* 防止flex布局时被压缩 */
    text-align: center; /* 水平居中 */
    padding-bottom: 1px; /* 微调视觉基线 */
  }

  .login-dialog {
    width: 95%;
    max-width: 320px;
  }
  
  .dialog-header {
    padding: 16px 20px;
  }
  
  .dialog-content {
    padding: 20px;
  }
  
  .form-group {
    margin-bottom: 16px;
  }
  
  .form-options {
    margin-bottom: 20px;
  }
}

@media (max-width: 480px) {
  .user-auth {
    margin-left: 8px;
  }
  
  .login-btn {
    padding: 4px 10px;
    font-size: 11px;
  }
  
  .user-avatar {
    width: 24px;
    height: 24px;
  }
  
  /* 首字母头像 - 480px断点响应式样式 */
  .avatar-fallback {
    width: 24px;
    height: 24px;
    font-size: 11px;
    line-height: 24px; /* 与容器高度一致，确保字母垂直居中 */
    flex-shrink: 0; /* 防止flex布局时被压缩 */
    text-align: center; /* 水平居中 */
    padding-bottom: 1px; /* 微调视觉基线 */
  }
}
</style>